/**
 * Copyright 2025 Design Barn Inc.
 */

'use client';

import { Button } from '@lottiefiles/ds-core';
import { IconEyeClosedOutline, IconEyeOpenOutline } from '@lottiefiles/icons';
import Link from 'next/link';
import React, { useState, useEffect, useCallback } from 'react';
import type { ControllerRenderProps, UseFormStateReturn } from 'react-hook-form';
import { Controller, useForm } from 'react-hook-form';

import Input from '../input/input';

const passwordIconSize = { width: 28, height: 28 };

export interface EmailPasswordFormInput {
  email: string;
  password: string;
}

export interface EmailPasswordFormProps {
  errors?: {
    email?: string;
    password?: string;
  };
  forgotPasswordLink?: string;
  formType?: string;
  onSubmit?: (data: EmailPasswordFormInput) => Promise<void>;
  showPasswordField?: boolean;
  showPasswordHelperText?: boolean;
  submitDisabled: boolean;
  submitLabel: string;
}

export default function EmailPasswordForm({
  errors,
  forgotPasswordLink,
  onSubmit,
  showPasswordField = true,
  showPasswordHelperText,
  submitDisabled,
  submitLabel,
}: EmailPasswordFormProps): React.ReactElement {
  const [showPassword, setShowPassword] = useState<boolean>(false);

  const {
    clearErrors,
    control,
    formState: { errors: formErrors, isValid },
    handleSubmit,
    resetField,
    setError,
    setFocus,
  } = useForm<EmailPasswordFormInput>({
    defaultValues: {
      email: '',
      password: '',
    },
  });

  useEffect(() => {
    setFocus('email');
  }, [setFocus]);

  useEffect(() => {
    if (errors?.email || formErrors.email?.message) {
      setError('email', { message: errors?.email || formErrors.email?.message });
    } else {
      clearErrors('email');
    }
    if (errors?.password || formErrors.password?.message) {
      setError('password', { message: errors?.password || formErrors.password?.message });
      resetField('password');
    } else {
      clearErrors('password');
    }

    if (!errors?.email && !errors?.password) {
      clearErrors();
    }
  }, [
    errors?.password,
    errors?.email,
    setError,
    clearErrors,
    formErrors.email?.message,
    formErrors.password?.message,
    resetField,
  ]);

  const renderEmailField = useCallback(
    ({
      field,
      formState,
    }: {
      field: ControllerRenderProps<EmailPasswordFormInput, 'email'>;
      formState: UseFormStateReturn<EmailPasswordFormInput>;
    }): React.ReactElement => (
      <Input
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...field}
        label="Email"
        placeholder="Email"
        type="email"
        errorText={formState.errors.email?.message || errors?.email}
      />
    ),
    [errors?.email],
  );

  const renderPasswordField = useCallback(
    ({
      field,
      formState,
    }: {
      field: ControllerRenderProps<EmailPasswordFormInput, 'password'>;
      formState: UseFormStateReturn<EmailPasswordFormInput>;
    }): React.ReactElement => (
      <Input
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...field}
        placeholder="Password"
        type={showPassword ? 'text' : 'password'}
        helperText={showPasswordHelperText ? 'Your password must include at least 8 characters' : undefined}
        // errorText={formState.errors.password?.message}
        errorText={formState.errors.password?.message || errors?.password}
        rightElement={
          // eslint-disable-next-line react-perf/jsx-no-jsx-as-prop
          <button
            type="button"
            className="flex h-[24px] w-[24px] items-center justify-center "
            // eslint-disable-next-line react-perf/jsx-no-new-function-as-prop
            onClick={(): void => {
              setShowPassword(!showPassword);
            }}
          >
            {showPassword ? (
              <IconEyeOpenOutline style={passwordIconSize} />
            ) : (
              <IconEyeClosedOutline style={passwordIconSize} />
            )}
          </button>
        }
      />
    ),
    [showPassword, showPasswordHelperText, errors?.password],
  );

  return (
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    <form onSubmit={onSubmit && handleSubmit(onSubmit)}>
      <div className="flex flex-col gap-4">
        <Controller
          name="email"
          control={control}
          render={renderEmailField}
          // eslint-disable-next-line react-perf/jsx-no-new-object-as-prop
          rules={{ required: 'Email is missing.' }}
        />
        {showPasswordField && (
          <Controller
            name="password"
            control={control}
            render={renderPasswordField}
            // eslint-disable-next-line react-perf/jsx-no-new-object-as-prop
            rules={{ required: 'Password is missing.' }}
          />
        )}
      </div>
      {Boolean(forgotPasswordLink) && (
        <div className="mt-2 flex items-center justify-end">
          <Link
            href={forgotPasswordLink as string}
            className="text-action-primary hover:text-action-primary-hover text-xs"
          >
            Forgot your password?
          </Link>
        </div>
      )}
      <Button
        id="button-login-submit"
        // eslint-disable-next-line react-perf/jsx-no-new-function-as-prop, @typescript-eslint/no-empty-function
        onClick={(): void => {}}
        variant="primary"
        size="lg"
        type="submit"
        disabled={!isValid}
        loading={submitDisabled}
        className="mb-[36px] mt-4 w-full rounded-[12px] text-base font-normal"
      >
        {submitLabel}
      </Button>
    </form>
  );
}
